/**
 * jimiService — typed wrapper around the JIMI hardware surface exposed by
 * fleet-api under /api/jimi/*. The console is independent of
 * BaliseManagement: it speaks directly to the vendor catalog, the
 * orchestration_balises join, command/video/alarm/track endpoints, and
 * never touches /api/bm/balises.
 *
 * All methods are thin: they assume fleet-api has already done auth,
 * normalization, and role gating. The UI never deals with the
 * X-Internal-Token used between fleet-api and jimi-receiver.
 */
import { apiClient } from '../config/api'

// ---------------------------------------------------------------------------
// Devices catalog (vendor + orchestration_balises join)
// ---------------------------------------------------------------------------

export type JimiDeviceType = 'SMART_LOCK' | 'GPS_TRACKER' | 'AI_DASHCAM' | string | null

export interface JimiDeviceRow {
  imei: string
  vendorId?: string | number | null
  name?: string | null
  model?: string | null
  vendorStatus?: string | null
  lastSeen?: string | null
  lat?: number | null
  lng?: number | null
  onboarded: boolean
  // orchestration-side fields (only present when onboarded === true)
  lockCode?: string
  universalId?: string
  deviceName?: string
  deviceType?: JimiDeviceType
  firmwareVersion?: string | null
  hardwareVersion?: string | null
  batteryLevel?: number | null
  signalStrength?: number | null
  status?: string
  syncStatus?: string
  assignmentStatus?: string
  lastSeenOrchestration?: string | null
  /** Currently-attached asset id (orchestration_balises.asset_id), null when free. */
  assetId?: number | null
  /** Convenience for the UI — assets.asset_code joined server-side. */
  assetCode?: string | null
  // raw vendor record kept for debugging / fallback
  _vendor?: Record<string, any>
}

export interface JimiDevicesResponse {
  ok: boolean
  checkedAt: string
  devices: JimiDeviceRow[]
  total: number
  onboardedCount: number
  vendorOnlyCount: number
  upstreamMessage?: string | null
  error?: string | null
}

// ---------------------------------------------------------------------------
// Events / location / track history
// ---------------------------------------------------------------------------

export interface JimiRawEvent {
  id?: number
  imei?: string
  topic?: string
  receivedAt?: string
  payload?: any
  parsed?: any
  [k: string]: any
}

export interface JimiEventsResponse {
  imei?: string
  count?: number
  events?: JimiRawEvent[]
  // upstream may pass-through other shapes; we keep the union narrow
  [k: string]: any
}

export interface JimiTrackPoint {
  lat: number
  lng: number
  ts?: string
  speed?: number | null
  heading?: number | null
  raw?: any
}

// ---------------------------------------------------------------------------
// Commands
// ---------------------------------------------------------------------------

export interface JimiCommandResponse {
  // TurboHive envelopes generally look like { code, message, data }; we
  // forward as-is so the UI can show whatever the vendor returned.
  code?: number
  message?: string
  data?: any
  [k: string]: any
}

export interface JimiCommandHistoryItem {
  cmdId?: string | number
  cmdNo?: number
  status?: string
  sentAt?: string
  ackAt?: string | null
  [k: string]: any
}

// ---------------------------------------------------------------------------
// Alarms
// ---------------------------------------------------------------------------

export interface JimiAlertRow {
  alertId?: string | number
  imei?: string
  alertType?: string
  beginTime?: string
  endTime?: string
  hasVideo?: boolean
  fileId?: string
  [k: string]: any
}

export interface JimiAlertSearchResponse {
  data?: { records?: JimiAlertRow[]; total?: number; pageNum?: number; pageSize?: number }
  records?: JimiAlertRow[]
  total?: number
  [k: string]: any
}

// ---------------------------------------------------------------------------
// Video
// ---------------------------------------------------------------------------

export interface JimiVideoLiveResponse {
  url?: string         // HLS or RTMP, depending on firmware
  protocol?: 'HLS' | 'RTMP' | 'FLV' | string
  expiresAt?: string
  [k: string]: any
}

export interface JimiVideoFileRow {
  fileId: string
  beginTime?: string
  endTime?: string
  durationSec?: number
  channel?: number
  [k: string]: any
}

export interface JimiVideoFilesResponse {
  data?: { records?: JimiVideoFileRow[] }
  records?: JimiVideoFileRow[]
  [k: string]: any
}

// ---------------------------------------------------------------------------
// OBD (engine telemetry from JIMI GPS trackers with an OBD harness)
// ---------------------------------------------------------------------------
//
// Vendor returns a flat metric dictionary; we keep the typed surface narrow
// to the metrics the UI explicitly knows about (see AssetObdWindow) and
// allow extra keys via `[k: string]: any` so a new vendor metric does not
// require touching this file.

export interface JimiObdLatest {
  acc?: number | string | null
  speed?: number | null
  engineSpeed?: number | null
  fuelLevel?: number | null
  batteryVoltage?: number | null
  coolantTemperature?: number | null
  intakeAirTemperature?: number | null
  throttlePosition?: number | null
  throttleAbsolutePosition?: number | null
  manifoldPressure?: number | null
  massAirFlow?: number | null
  instantFuelConsumption?: number | null
  averageFuelConsumption?: number | null
  totalFuelConsumption?: number | null
  engineRuntime?: number | null
  odometer?: number | null
  cumulativeMileage?: number | null
  vehicleType?: string | null
  ts?: string
  [k: string]: any
}

export interface JimiObdHistoryRow extends JimiObdLatest {
  id?: number | string
}

export interface JimiObdHistoryResponse {
  data?: { records?: JimiObdHistoryRow[]; total?: number; pageNum?: number; pageSize?: number }
  records?: JimiObdHistoryRow[]
  [k: string]: any
}

// ---------------------------------------------------------------------------
// Tags (paired BLE peripherals — driver tags, cargo / temperature tags, …)
// ---------------------------------------------------------------------------

export interface JimiTagRow {
  tagId: string
  type?: string | null
  name?: string | null
  rssi?: number | null
  battery?: number | null
  /** Latest measured value, semantics depend on tag type. */
  value?: number | string | null
  unit?: string | null
  lastSeen?: string | null
  [k: string]: any
}

export interface JimiTagListResponse {
  data?: { records?: JimiTagRow[] }
  records?: JimiTagRow[]
  [k: string]: any
}

export interface JimiTagHistoryRow {
  ts?: string
  value?: number | string | null
  rssi?: number | null
  battery?: number | null
  [k: string]: any
}

export interface JimiTagHistoryResponse {
  data?: { records?: JimiTagHistoryRow[]; total?: number; pageNum?: number; pageSize?: number }
  records?: JimiTagHistoryRow[]
  [k: string]: any
}

// ---------------------------------------------------------------------------
// Service
// ---------------------------------------------------------------------------

const enc = encodeURIComponent

export const jimiService = {
  // events / location / track ---------------------------------------------
  async getEvents(imei: string): Promise<JimiEventsResponse> {
    const { data } = await apiClient.get(`/jimi/devices/${enc(imei)}/events`)
    return data as JimiEventsResponse
  },

  async getLocation(imei: string): Promise<any> {
    const { data } = await apiClient.get(`/jimi/devices/${enc(imei)}/location`)
    return data
  },

  async getTrackHistory(imei: string, from?: string, to?: string): Promise<any> {
    const params: Record<string, string> = {}
    if (from) params.from = from
    if (to)   params.to   = to
    const { data } = await apiClient.get(`/jimi/devices/${enc(imei)}/track`, params)
    return data
  },

  // commands --------------------------------------------------------------
  async commandSeal(imei: string): Promise<JimiCommandResponse> {
    const { data } = await apiClient.post(`/jimi/devices/${enc(imei)}/command/seal`, {})
    return data
  },
  async commandUnseal(imei: string): Promise<JimiCommandResponse> {
    const { data } = await apiClient.post(`/jimi/devices/${enc(imei)}/command/unseal`, {})
    return data
  },
  async commandWakeup(imei: string): Promise<JimiCommandResponse> {
    const { data } = await apiClient.post(`/jimi/devices/${enc(imei)}/command/wakeup`, {})
    return data
  },
  async commandSend(imei: string, cmdNo: number, params?: Record<string, any>): Promise<JimiCommandResponse> {
    const { data } = await apiClient.post(`/jimi/devices/${enc(imei)}/command/send`, { cmdNo, params })
    return data
  },
  async commandsCatalog(): Promise<any> {
    const { data } = await apiClient.get('/jimi/commands/catalog')
    return data
  },
  async commandsHistory(imei: string, page = 1, size = 50): Promise<any> {
    const { data } = await apiClient.get(`/jimi/devices/${enc(imei)}/command/history`, { page, size })
    return data
  },
  async commandsOffline(imei: string): Promise<any> {
    const { data } = await apiClient.get(`/jimi/devices/${enc(imei)}/command/offline`)
    return data
  },
  async commandsOfflineCancel(imei: string, cmdId: string | number): Promise<any> {
    const { data } = await apiClient.post(`/jimi/devices/${enc(imei)}/command/offline/${enc(String(cmdId))}/cancel`, {})
    return data
  },

  // alarms ----------------------------------------------------------------
  async searchAlerts(opts: {
    imei?: string; from?: string; to?: string; type?: string; hasVideo?: boolean;
    page?: number; size?: number;
  } = {}): Promise<JimiAlertSearchResponse> {
    const params: Record<string, string | number> = {}
    if (opts.imei)     params.imei = opts.imei
    if (opts.from)     params.from = opts.from
    if (opts.to)       params.to   = opts.to
    if (opts.type)     params.type = opts.type
    if (opts.hasVideo != null) params.hasVideo = String(opts.hasVideo)
    if (opts.page)     params.page = opts.page
    if (opts.size)     params.size = opts.size
    const { data } = await apiClient.get('/jimi/alerts', params)
    return data as JimiAlertSearchResponse
  },
  async alertVideo(alertId: string | number): Promise<any> {
    const { data } = await apiClient.get(`/jimi/alerts/${enc(String(alertId))}/video`)
    return data
  },

  // video -----------------------------------------------------------------
  async videoLiveStart(imei: string, channel?: number): Promise<JimiVideoLiveResponse> {
    const { data } = await apiClient.post(`/jimi/devices/${enc(imei)}/video/live/start`,
      channel != null ? { channel } : {})
    return data as JimiVideoLiveResponse
  },
  async videoLiveStop(imei: string): Promise<any> {
    const { data } = await apiClient.post(`/jimi/devices/${enc(imei)}/video/live/stop`, {})
    return data
  },
  async videoCapture(imei: string, channel?: number): Promise<any> {
    const { data } = await apiClient.post(`/jimi/devices/${enc(imei)}/video/capture`,
      channel != null ? { channel } : {})
    return data
  },
  async videoFiles(imei: string, from?: string, to?: string): Promise<JimiVideoFilesResponse> {
    const params: Record<string, string> = {}
    if (from) params.from = from
    if (to)   params.to   = to
    const { data } = await apiClient.get(`/jimi/devices/${enc(imei)}/video/files`, params)
    return data as JimiVideoFilesResponse
  },
  async videoPlaybackStart(imei: string, fileId: string): Promise<any> {
    const { data } = await apiClient.post(`/jimi/devices/${enc(imei)}/video/playback/start`, { fileId })
    return data
  },
  async videoPlaybackStop(imei: string): Promise<any> {
    const { data } = await apiClient.post(`/jimi/devices/${enc(imei)}/video/playback/stop`, {})
    return data
  },
  async videoPlaybackControl(imei: string, action: 'play' | 'pause' | 'seek', offsetSec?: number): Promise<any> {
    const body: Record<string, any> = { action }
    if (offsetSec != null) body.offsetSec = offsetSec
    const { data } = await apiClient.post(`/jimi/devices/${enc(imei)}/video/playback/control`, body)
    return data
  },

  // OBD (engine telemetry) ------------------------------------------------
  async getObdLatest(imei: string): Promise<JimiObdLatest> {
    const { data } = await apiClient.get(`/jimi/devices/${enc(imei)}/obd/latest`)
    // Vendor sometimes wraps the metric map under `data` — unwrap when present.
    if (data && typeof data === 'object' && data.data && typeof data.data === 'object') {
      return data.data as JimiObdLatest
    }
    return data as JimiObdLatest
  },

  async getObdHistory(imei: string, opts: { from?: string; to?: string; page?: number; size?: number } = {}): Promise<JimiObdHistoryResponse> {
    const params: Record<string, string | number> = {}
    if (opts.from) params.from = opts.from
    if (opts.to)   params.to   = opts.to
    if (opts.page) params.page = opts.page
    if (opts.size) params.size = opts.size
    const { data } = await apiClient.get(`/jimi/devices/${enc(imei)}/obd/history`, params)
    return data as JimiObdHistoryResponse
  },

  // Tags (paired BLE peripherals) ----------------------------------------
  async getTags(imei: string): Promise<JimiTagListResponse> {
    const { data } = await apiClient.get(`/jimi/devices/${enc(imei)}/tags`)
    return data as JimiTagListResponse
  },

  async getTagHistory(imei: string, tagId: string, opts: { from?: string; to?: string; page?: number; size?: number } = {}): Promise<JimiTagHistoryResponse> {
    const params: Record<string, string | number> = {}
    if (opts.from) params.from = opts.from
    if (opts.to)   params.to   = opts.to
    if (opts.page) params.page = opts.page
    if (opts.size) params.size = opts.size
    const { data } = await apiClient.get(`/jimi/devices/${enc(imei)}/tags/${enc(tagId)}/history`, params)
    return data as JimiTagHistoryResponse
  },

  // -------------------------------------------------------------------------
  // Asset attachment — UI-driven flow (replaces manual SQL).
  // The Java endpoints already exist on balise-management; we go through the
  // /api/bm proxy that fleet-api configures with a service-token signer.
  // -------------------------------------------------------------------------

  /** Attach a JIMI balise (by lock_code) to an asset. */
  async attachToAsset(lockCode: string, assetId: number): Promise<{ success?: boolean; [k: string]: any }> {
    const { data } = await apiClient.post(`/bm/balises/${enc(lockCode)}/assign-asset`, { assetId })
    return data
  },

  /** Detach a JIMI balise (by lock_code) from its current asset/customer. */
  async detachFromAsset(lockCode: string): Promise<{ success?: boolean; [k: string]: any }> {
    const { data } = await apiClient.post(`/bm/balises/${enc(lockCode)}/unassign-customer`, {})
    return data
  },

  /**
   * Lightweight asset list for the attach picker. Goes to the same fleet-api
   * endpoint the Fleet Map already uses, so the picker stays consistent with
   * what the user sees elsewhere in the portal.
   */
  async listAssetsForAttach(): Promise<JimiAttachableAsset[]> {
    const { data } = await apiClient.get('/assets')
    if (!Array.isArray(data)) return []
    return data.map((a: any) => ({
      id: Number(a.id),
      assetCode: a.asset_code || `ASSET-${a.id}`,
      assetType: a.asset_type || null,
      brand: a.brand || null,
      model: a.model || null,
      licensePlate: a.license_plate || null,
      status: a.status || null,
      customerName: a.customer_name || null,
    }))
  },
}

/** Row shape consumed by AttachAssetModal. */
export interface JimiAttachableAsset {
  id: number
  assetCode: string
  assetType: string | null
  brand: string | null
  model: string | null
  licensePlate: string | null
  status: string | null
  customerName: string | null
}

// ---------------------------------------------------------------------------
// Helpers used by the console card grid
// ---------------------------------------------------------------------------

export function inferJimiDeviceType(row: JimiDeviceRow): JimiDeviceType {
  if (row.deviceType) return row.deviceType
  const m = (row.model || '').toUpperCase()
  if (m.includes('LL01') || m.includes('LOCK')) return 'SMART_LOCK'
  if (m.includes('VL103') || m.includes('VL') || m.includes('TRACK')) return 'GPS_TRACKER'
  if (m.includes('JC450') || m.includes('CAM') || m.includes('DASH')) return 'AI_DASHCAM'
  return null
}

export function jimiDeviceTypeLabel(t: JimiDeviceType): string {
  switch (t) {
    case 'SMART_LOCK': return 'Smart Lock'
    case 'GPS_TRACKER': return 'GPS Tracker'
    case 'AI_DASHCAM':  return 'AI Dashcam'
    default:            return 'Unclassified'
  }
}
